import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Dashboard.css';

const API_URL = process.env.REACT_APP_API_URL || '';
const getToken = () => localStorage.getItem('notes_token') || '';
const fetchWithAuth = (url, options = {}) => {
  return fetch(url, { ...options, headers: { ...options.headers, 'x-token': getToken() } });
};

export default function LegalReport() {
  const navigate = useNavigate();
  const [reportType, setReportType] = useState('daily');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // 设置默认日期范围
    const today = new Date();
    const monday = new Date(today);
    monday.setDate(today.getDate() - (today.getDay() || 7) + 1);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    setStartDate(monday.toISOString().split('T')[0]);
    setEndDate(sunday.toISOString().split('T')[0]);
  }, []);

  const generateReport = async () => {
    setLoading(true);
    setReport(null);
    try {
      let url = '';
      if (reportType === 'daily') {
        url = `${API_URL}/api/law/report/daily?date=${date}`;
      } else if (reportType === 'weekly') {
        url = `${API_URL}/api/law/report/daily?startDate=${startDate}&endDate=${endDate}`;
      } else {
        // 年报使用月份范围
        const year = new Date().getFullYear();
        url = `${API_URL}/api/law/report/daily?startDate=${year}-01-01&endDate=${year}-12-31`;
      }
      const res = await fetchWithAuth(url);
      const data = await res.json();
      setReport(data);
    } catch (e) {
      alert('生成报告失败: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    if (!report) return;
    alert('导出功能开发中，当前请复制文本内容');
  };

  return (
    <div className="legal-report">
      <div className="report-header" style={{ marginBottom: 20 }}>
        <h2 style={{ margin: 0 }}>📊 工作报表</h2>
        <p style={{ margin: '4px 0 0', color: '#666', fontSize: 14 }}>
          生成日报/周报/年报，支持导出Word/PDF
        </p>
      </div>

      {/* 报表类型选择 */}
      <div className="report-type-selector" style={{
        display: 'flex', gap: 8, marginBottom: 20
      }}>
        {[
          { key: 'daily', label: '📅 日报', desc: '今日工作汇总' },
          { key: 'weekly', label: '📆 周报', desc: '本周工作汇总' },
          { key: 'monthly', label: '📈 月报', desc: '本月工作汇总' },
          { key: 'yearly', label: '📊 年报', desc: '全年工作汇总' }
        ].map(t => (
          <div key={t.key}
            onClick={() => setReportType(t.key)}
            style={{
              flex: 1, padding: '12px 16px', borderRadius: 8, cursor: 'pointer',
              border: reportType === t.key ? '2px solid #1a73e8' : '1px solid #e8e8e8',
              background: reportType === t.key ? '#f0f7ff' : 'white',
              textAlign: 'center', transition: 'all 0.2s'
            }}
          >
            <div style={{ fontSize: 20, marginBottom: 4 }}>{t.label.split(' ')[0]}</div>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{t.label.split(' ')[1]}</div>
            <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>{t.desc}</div>
          </div>
        ))}
      </div>

      {/* 日期选择 */}
      <div className="report-date-selector" style={{
        background: 'white', borderRadius: 12, padding: 16, marginBottom: 20,
        border: '1px solid #e8e8e8', display: 'flex', alignItems: 'center', gap: 12
      }}>
        {reportType === 'daily' ? (
          <>
            <label style={{ fontSize: 14, fontWeight: 500 }}>日期：</label>
            <input type="date" value={date} onChange={e => setDate(e.target.value)}
              style={{ padding: 6, border: '1px solid #ddd', borderRadius: 6 }} />
          </>
        ) : reportType === 'weekly' ? (
          <>
            <label style={{ fontSize: 14, fontWeight: 500 }}>本周：</label>
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
              style={{ padding: 6, border: '1px solid #ddd', borderRadius: 6 }} />
            <span style={{ color: '#999' }}>至</span>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
              style={{ padding: 6, border: '1px solid #ddd', borderRadius: 6 }} />
          </>
        ) : (
          <span style={{ fontSize: 14, color: '#666' }}>
            年度：{new Date().getFullYear()}年
          </span>
        )}
        <button onClick={generateReport} disabled={loading} style={{
          marginLeft: 'auto', padding: '8px 20px', border: 'none', borderRadius: 6,
          background: loading ? '#ccc' : '#1a73e8', color: 'white',
          cursor: loading ? 'not-allowed' : 'pointer', fontWeight: 500
        }}>
          {loading ? '生成中...' : '生成报表'}
        </button>
      </div>

      {/* 报表内容 */}
      {report && (
        <div className="report-content" style={{
          background: 'white', borderRadius: 12, padding: 24,
          border: '1px solid #e8e8e8', boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ margin: 0, fontSize: 18 }}>
              {reportType === 'daily' ? `${report.date} 工作日报` :
               reportType === 'weekly' ? `${startDate} ~ ${endDate} 工作周报` :
               `${new Date().getFullYear()}年 工作年报`}
            </h3>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => handleExport('word')} style={{
                padding: '6px 12px', border: '1px solid #ddd', borderRadius: 6,
                background: 'white', cursor: 'pointer', fontSize: 13
              }}>📄 导出Word</button>
              <button onClick={() => handleExport('pdf')} style={{
                padding: '6px 12px', border: '1px solid #ddd', borderRadius: 6,
                background: 'white', cursor: 'pointer', fontSize: 13
              }}>📕 导出PDF</button>
            </div>
          </div>

          {/* 摘要 */}
          {report.summary && (
            <div className="report-summary" style={{
              background: '#fafafa', borderRadius: 8, padding: 16, marginBottom: 16,
              fontSize: 14, lineHeight: 1.8, whiteSpace: 'pre-wrap', color: '#333'
            }}>
              {report.summary}
            </div>
          )}

          {/* 工作记录明细 */}
          {report.logs && report.logs.length > 0 && (
            <div className="report-details">
              <h4 style={{ fontSize: 15, marginBottom: 12 }}>📝 工作记录明细</h4>
              {report.logs.map((log, idx) => (
                <div key={log.id || idx} style={{
                  background: '#f8f9fa', borderRadius: 6, padding: 12, marginBottom: 8,
                  borderLeft: '3px solid #1a73e8'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontWeight: 500, fontSize: 14 }}>{log.recordType}</span>
                    <span style={{ fontSize: 12, color: '#999' }}>{log.date}</span>
                  </div>
                  <div style={{ fontSize: 13, lineHeight: 1.6, color: '#333' }}>
                    {log.content}
                  </div>
                  {log.duration && (
                    <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>耗时：{log.duration}h</div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* 按案件分组 */}
          {report.caseGroups && Object.keys(report.caseGroups).length > 0 && (
            <div className="report-by-case" style={{ marginTop: 16 }}>
              <h4 style={{ fontSize: 15, marginBottom: 12 }}>⚖️ 按案件分组</h4>
              {Object.entries(report.caseGroups).map(([caseTitle, logs]) => (
                <div key={caseTitle} style={{
                  background: '#f0f7ff', borderRadius: 8, padding: 12, marginBottom: 8
                }}>
                  <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}>{caseTitle}</div>
                  {logs.map((log, idx) => (
                    <div key={idx} style={{ fontSize: 13, color: '#333', marginBottom: 4 }}>
                      • {log.recordType}：{log.content.substring(0, 50)}{log.content.length > 50 ? '...' : ''}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
